// Copyright © 2026 Phaena Management B.V. All rights reserved.
// Proprietary and confidential. See COPYRIGHT.md in the repository root.

/**
 * text-analytics brand mark.
 *
 * Two-part: a small icon of three stacked "text rows" with the
 * middle row swiped in highlighter amber (the product, in one
 * glyph: open-text being marked up) + the wordmark "text-analytics"
 * set in Spectral. The amber underline-dot after the wordmark
 * carries the accent even when the icon is cropped (footer chip,
 * favicon).
 */

type Size = "sm" | "md" | "lg" | "xl"

const SIZES: Record<Size, { svg: number; word: string; gap: string }> = {
  sm: { svg: 16, word: "text-base", gap: "gap-1.5" },
  md: { svg: 22, word: "text-xl", gap: "gap-2" },
  lg: { svg: 32, word: "text-3xl", gap: "gap-2.5" },
  xl: { svg: 52, word: "text-5xl", gap: "gap-3" },
}

interface Props {
  size?: Size
  /** Hide the wordmark — for the favicon, footer chip, etc. */
  iconOnly?: boolean
  className?: string
}

const AMBER = "oklch(0.86 0.15 90)"

export function Wordmark({ size = "md", iconOnly = false, className = "" }: Props) {
  const s = SIZES[size]
  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <svg
        width={s.svg}
        height={s.svg}
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden
        className="shrink-0"
      >
        {/* Highlighter swipe behind the middle text row. */}
        <rect x="3" y="14.5" width="22" height="5" rx="1" fill={AMBER} opacity={0.9} />
        {/* Three text rows — top, highlighted middle, bottom. */}
        <rect x="4" y="7" width="20" height="2.4" rx="1.2" fill="currentColor" opacity={0.85} />
        <rect x="4" y="15.3" width="24" height="2.6" rx="1.3" fill="currentColor" />
        <rect x="4" y="23" width="16" height="2.4" rx="1.2" fill="currentColor" opacity={0.85} />
      </svg>
      {!iconOnly && (
        <span
          className={`font-display font-semibold tracking-tight leading-none ${s.word}`}
          style={{ fontFamily: '"Spectral", "Iowan Old Style", Georgia, serif' }}
        >
          Wren
          <span
            aria-hidden
            className="inline-block ml-0.5 align-baseline"
            style={{ color: AMBER }}
          >
            .
          </span>
        </span>
      )}
    </span>
  )
}
